import {
  DEFAULT_TASK_TRACKER_BOARD_COMPLETED_STATUS_FILTER,
  isTaskTrackerStickerFilterDraftEmpty,
  mapStickerFiltersDraftToApi,
  normalizeTaskTrackerStickerFilterValue,
  normalizeTaskTrackerStickerFilterValues,
  TaskTrackerStickerFilterDraftValue,
  TaskTrackerStickerFiltersDraft,
} from './taskTrackerBoard.stickerFilters.utils'
import { readStoredBoardCompletedStatusFilter } from './taskTrackerBoardCompletedStatusFilters.storage'

export const TASK_TRACKER_BOARD_STICKER_FILTERS_STORAGE_KEY =
  'task-tracker-crm:board-sticker-filters'

type StoredBoardStickerFiltersMap = Record<string, unknown>

const normalizeStoredDraftValue = (
  value: unknown,
): TaskTrackerStickerFilterDraftValue | null => {
  if (value == null) return null

  if (typeof value === 'string') {
    const normalized = normalizeTaskTrackerStickerFilterValue(value)
    return normalized ?? null
  }

  if (Array.isArray(value)) {
    const values = normalizeTaskTrackerStickerFilterValues(
      value.filter((item): item is string => typeof item === 'string'),
    )
    return values.length > 0 ? values : null
  }

  if (typeof value === 'object') {
    const record = value as { from?: unknown; to?: unknown }
    const from = normalizeTaskTrackerStickerFilterValue(
      typeof record.from === 'string' ? record.from : null,
    )
    const to = normalizeTaskTrackerStickerFilterValue(
      typeof record.to === 'string' ? record.to : null,
    )

    if (from == null && to == null) return null

    return { from, to }
  }

  return null
}

const parseStoredBoardStickerFiltersDraft = (
  raw: unknown,
): TaskTrackerStickerFiltersDraft => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {}
  }

  const draft: TaskTrackerStickerFiltersDraft = {}

  Object.entries(raw).forEach(([key, value]) => {
    const fieldSettingId = Number(key)
    if (!Number.isFinite(fieldSettingId)) return

    const normalizedValue = normalizeStoredDraftValue(value)
    if (
      normalizedValue == null ||
      isTaskTrackerStickerFilterDraftEmpty(normalizedValue)
    ) {
      return
    }

    draft[fieldSettingId] = normalizedValue
  })

  return draft
}

const readStoredBoardStickerFiltersMap = (): StoredBoardStickerFiltersMap => {
  if (typeof window === 'undefined') return {}

  try {
    const raw = window.localStorage.getItem(
      TASK_TRACKER_BOARD_STICKER_FILTERS_STORAGE_KEY,
    )
    if (!raw) return {}

    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {}
    }

    return parsed as StoredBoardStickerFiltersMap
  } catch {
    return {}
  }
}

export const readStoredBoardStickerFilters = (
  boardId: number,
): TaskTrackerStickerFiltersDraft => {
  const stored = readStoredBoardStickerFiltersMap()[String(boardId)]
  if (stored == null) return {}

  return parseStoredBoardStickerFiltersDraft(stored)
}

export const writeStoredBoardStickerFilters = (
  boardId: number,
  draft: TaskTrackerStickerFiltersDraft,
) => {
  if (typeof window === 'undefined') return

  try {
    const stored = readStoredBoardStickerFiltersMap()
    const hasFilters = mapStickerFiltersDraftToApi(draft).length > 0

    if (hasFilters) {
      stored[String(boardId)] = draft
    } else {
      delete stored[String(boardId)]
    }

    if (Object.keys(stored).length === 0) {
      window.localStorage.removeItem(TASK_TRACKER_BOARD_STICKER_FILTERS_STORAGE_KEY)
      return
    }

    window.localStorage.setItem(
      TASK_TRACKER_BOARD_STICKER_FILTERS_STORAGE_KEY,
      JSON.stringify(stored),
    )
  } catch {
    // ignore quota / privacy mode errors
  }
}

export const createBoardStickerFiltersState = (boardId: number | null) => {
  const draftFilters =
    boardId != null ? readStoredBoardStickerFilters(boardId) : {}
  const completedStatus =
    boardId != null
      ? readStoredBoardCompletedStatusFilter(boardId)
      : DEFAULT_TASK_TRACKER_BOARD_COMPLETED_STATUS_FILTER

  return {
    draftFilters,
    appliedFilters: mapStickerFiltersDraftToApi(draftFilters),
    completedStatus,
  }
}
